/**
 * Request policy for trip-service search endpoints ($search on the database). Kept free of
 * an `axios` import so sagas, action creators and their tests can use it directly.
 */

const DEFAULT_SEARCH_MAX_RETRIES = 1;
const MAX_SEARCH_RETRIES = 3;
const RETRYABLE_SEARCH_STATUSES = new Set([502, 503]);
const BASE_RETRY_DELAY_MS = 1000;
const MAX_RETRY_JITTER_MS = 500;

export const SEARCH_ERROR_KINDS = Object.freeze({
  TIME_LIMIT: 'timeLimit',
  RESULT_LIMIT: 'resultLimit',
  LOAD_FAILED: 'loadFailed',
});

/**
 * Parses the `SEARCH_MAX_RETRIES` build setting. Unset or invalid falls back to 1, and the
 * value is capped at 3 so a misconfiguration cannot turn a degraded backend into a retry storm.
 *
 * @param {string | undefined} rawValue
 * @returns {number}
 */
export const resolveSearchMaxRetries = (rawValue) => {
  const parsed = Number.parseInt(rawValue, 10);
  if (Number.isNaN(parsed) || parsed < 0) return DEFAULT_SEARCH_MAX_RETRIES;
  return Math.min(parsed, MAX_SEARCH_RETRIES);
};

/**
 * True for a request the caller cancelled (AbortController), which must never be retried
 * or reported.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isCancelledRequest = (error) =>
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

/**
 * Retry only failures a second attempt can fix: a connection error with no response, or a
 * 502/503. Never a cancelled or timed-out request, a 4xx (including the trip service's
 * `search_timeout`) or any other 5xx: those would only add load to a struggling backend.
 * A gateway 504 without CORS headers reaches the browser as a connection error, so it can
 * be retried once; the cap keeps that bounded.
 *
 * @param {import('axios').AxiosError} error
 * @returns {boolean}
 */
export const isRetryableSearchError = (error) => {
  if (isCancelledRequest(error)) return false;
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT')
    return false;
  if (!error?.response) return true;
  return RETRYABLE_SEARCH_STATUSES.has(error.response.status);
};

/**
 * Exponential backoff from 1s with up to 0.5s of jitter, so retries from many open tabs do
 * not land on the backend at the same moment.
 *
 * @param {number} retryCount - 1 for the first retry
 * @returns {number} delay in milliseconds
 */
export const searchRetryDelay = (retryCount) =>
  BASE_RETRY_DELAY_MS * 2 ** (retryCount - 1) +
  Math.random() * MAX_RETRY_JITTER_MS;

/**
 * Per-request axios config for search calls; overrides the instance-wide retry policy.
 */
export const searchRetryConfig = Object.freeze({
  'axios-retry': {
    retries: resolveSearchMaxRetries(process.env.SEARCH_MAX_RETRIES),
    retryCondition: isRetryableSearchError,
    retryDelay: searchRetryDelay,
  },
});

/**
 * Classifies a failed search for the failures that have approved in-table copy. Anything
 * else (a 403, a validation error) returns `null` and keeps the caller's existing handling,
 * which shows the server's own message.
 *
 * A load failure must be an axios error: an exception thrown by our own code inside a
 * saga's `try` (e.g. a TypeError on an unexpected response shape) has no `response`
 * either, and must keep the error toast rather than pass for a network blip.
 *
 * @param {import('axios').AxiosError} error
 * @returns {'timeLimit' | 'resultLimit' | 'loadFailed' | null}
 */
export const getSearchErrorKind = (error) => {
  if (isCancelledRequest(error)) return null;

  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 422 && data?.error === 'search_timeout') {
    return SEARCH_ERROR_KINDS.TIME_LIMIT;
  }
  if (
    status === 422 &&
    data?.error_details?.reason === 'result_window_exceeded'
  ) {
    return SEARCH_ERROR_KINDS.RESULT_LIMIT;
  }
  if (error?.isAxiosError && (!error.response || status >= 500)) {
    return SEARCH_ERROR_KINDS.LOAD_FAILED;
  }
  return null;
};
